// Hand-rolled hash router. Hash routing is the zero-lock-in move: every route
// lives under `#/…`, so no host needs rewrite rules and moving hosts is a file
// copy. The back button works inside the installed app for free.

export type Route =
  | { name: 'threads' }
  | { name: 'add'; phone?: string }
  | { name: 'conversation'; accountId: string }
  | { name: 'contact'; accountId: string }
  | { name: 'orders'; accountId: string }
  | { name: 'view'; accountId: string; viewId: string }
  | { name: 'staffLogin' }
  | { name: 'staff' }
  | { name: 'staffViews'; threadId: string }
  | { name: 'settings' }
  | { name: 'diagnostics' };

function parse(hash: string): Route {
  const parts = hash.replace(/^#\/?/, '').split('/').filter((p) => p.length > 0);
  switch (parts[0]) {
    case 'add':
      // The optional segment prefills the number — the re-verification path.
      return parts[1] !== undefined
        ? { name: 'add', phone: decodeURIComponent(parts[1]) }
        : { name: 'add' };
    case 'thread':
      return parts[1] !== undefined
        ? { name: 'conversation', accountId: decodeURIComponent(parts[1]) }
        : { name: 'threads' };
    case 'contact':
      return parts[1] !== undefined
        ? { name: 'contact', accountId: decodeURIComponent(parts[1]) }
        : { name: 'threads' };
    case 'orders':
      return parts[1] !== undefined
        ? { name: 'orders', accountId: decodeURIComponent(parts[1]) }
        : { name: 'threads' };
    case 'view':
      // Both segments or nothing: a page without an id is not a page, and guessing
      // which one was meant would open the wrong company's content.
      return parts[1] !== undefined && parts[2] !== undefined
        ? {
            name: 'view',
            accountId: decodeURIComponent(parts[1]),
            viewId: decodeURIComponent(parts[2]),
          }
        : { name: 'threads' };
    case 'staff':
      // Reached by typing the address. There is no link to it from the buyer's app,
      // because it is not for the buyer.
      if (parts[1] === 'login') {
        return { name: 'staffLogin' };
      }
      return parts[1] === 'thread' && parts[2] !== undefined
        ? { name: 'staffViews', threadId: decodeURIComponent(parts[2]) }
        : { name: 'staff' };
    case 'settings':
      return { name: 'settings' };
    case 'diagnostics':
      return { name: 'diagnostics' };
    default:
      return { name: 'threads' };
  }
}

function toHash(route: Route): string {
  switch (route.name) {
    case 'threads':
      return '#/';
    case 'add':
      return route.phone !== undefined ? `#/add/${encodeURIComponent(route.phone)}` : '#/add';
    case 'conversation':
      return `#/thread/${encodeURIComponent(route.accountId)}`;
    case 'contact':
      return `#/contact/${encodeURIComponent(route.accountId)}`;
    case 'orders':
      return `#/orders/${encodeURIComponent(route.accountId)}`;
    case 'view':
      return `#/view/${encodeURIComponent(route.accountId)}/${encodeURIComponent(route.viewId)}`;
    case 'staffLogin':
      return '#/staff/login';
    case 'staff':
      return '#/staff';
    case 'staffViews':
      return `#/staff/thread/${encodeURIComponent(route.threadId)}`;
    case 'settings':
      return '#/settings';
    case 'diagnostics':
      return '#/diagnostics';
  }
}

const state = $state({ route: parse(location.hash) });

window.addEventListener('hashchange', () => {
  state.route = parse(location.hash);
});

export function currentRoute(): Route {
  return state.route;
}

export function navigate(route: Route): void {
  location.hash = toHash(route);
}

export function goBack(): void {
  if (history.length > 1) {
    history.back();
  } else {
    navigate({ name: 'threads' });
  }
}
